// Probability Engine — Platt-calibrated probabilities from signal model output.
// BET/PASS/FADE signals + Kelly position sizing + prediction log.

import type { Tab } from './index';
import { esc, timeAgo } from '../utils';
import { workerFetch } from '../worker';
import { showToast } from '../toast';
import { store } from '../state';
import type { Ticker } from '../state';

interface ProbResult {
  ticker:      string;
  probability: number;
  signal:      'BET' | 'PASS' | 'FADE';
  kelly:       number;
  confidence:  number;
  components:  { momentum: number; sentiment: number; fundamentals: number; };
  factors:     { momentum: string[]; sentiment: string[]; fundamentals: string[]; };
  rawScore:    number;
  price:       number;
  rsi:         number;
  change20d:   number;
  history:     Array<{ ts: string; prob: number; signal: string; price: number; }>;
}
interface ProbResp { results: ProbResult[]; cachedAt: string; }

const SIG_COLOR = { BET: 'var(--green)', PASS: 'var(--warn)', FADE: 'var(--red)' };
const SIG_BG    = { BET: 'rgba(46,213,115,0.1)', PASS: 'rgba(255,193,7,0.1)', FADE: 'rgba(255,71,87,0.1)' };

function probGauge(p: number, size = 64): string {
  const pct  = Math.round(p * 100);
  const col  = p >= 0.62 ? 'var(--green)' : p <= 0.45 ? 'var(--red)' : 'var(--warn)';
  const r    = size / 2 - 6;
  const circ = 2 * Math.PI * r;
  const dash = (p * circ);
  return `
    <svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" style="display:block;">
      <circle cx="${size/2}" cy="${size/2}" r="${r}" fill="none" stroke="var(--border)" stroke-width="5"/>
      <circle cx="${size/2}" cy="${size/2}" r="${r}" fill="none" stroke="${col}" stroke-width="5"
        stroke-dasharray="${dash.toFixed(1)} ${circ.toFixed(1)}"
        transform="rotate(-90 ${size/2} ${size/2})" stroke-linecap="round"/>
      <text x="${size/2}" y="${size/2 + 5}" text-anchor="middle"
        style="font-family:var(--mono);font-size:13px;fill:${col};font-weight:700;">${pct}%</text>
    </svg>`;
}

function compBar(p: number, label: string): string {
  const pct = Math.round(p * 100);
  const col = p >= 0.6 ? 'var(--green)' : p <= 0.4 ? 'var(--red)' : 'var(--warn)';
  return `
    <div style="margin-bottom:6px;">
      <div style="display:flex;justify-content:space-between;font-size:10px;color:var(--text3);margin-bottom:2px;">
        <span>${esc(label)}</span><span style="color:${col};">${pct}%</span>
      </div>
      <div style="height:4px;background:var(--bg3);border-radius:2px;overflow:hidden;">
        <div style="width:${pct}%;height:100%;background:${col};border-radius:2px;"></div>
      </div>
    </div>`;
}

export class ProbabilityTab implements Tab {
  readonly id = 'probability';
  private currentTickers: string[] = [];

  private el(id: string): HTMLElement { return document.getElementById(id) as HTMLElement; }

  init(): void {
    document.getElementById('prob-analyze-btn')?.addEventListener('click', () => this.analyzeInput());
    document.getElementById('prob-portfolio-btn')?.addEventListener('click', () => this.loadPortfolio());
    (document.getElementById('prob-ticker-input') as HTMLInputElement | null)
      ?.addEventListener('keydown', (e: KeyboardEvent) => { if (e.key === 'Enter') this.analyzeInput(); });

    document.getElementById('prob-body')?.addEventListener('click', (e: MouseEvent) => {
      const row = (e.target as HTMLElement).closest<HTMLElement>('[data-prob-expand]');
      if (row && !(e.target as HTMLElement).closest('button')) {
        const detail = document.getElementById('prob-detail-' + row.dataset.probExpand);
        if (detail) detail.style.display = detail.style.display === 'none' ? '' : 'none';
      }
    });
  }

  render(): void {
    if (!this.currentTickers.length) this.loadPortfolio();
  }

  private analyzeInput(): void {
    const raw = (document.getElementById('prob-ticker-input') as HTMLInputElement)?.value.trim().toUpperCase() ?? '';
    if (!raw) return;
    const tickers = raw.split(',').map(t => t.trim()).filter(Boolean).slice(0, 10);
    this.load(tickers);
  }

  private loadPortfolio(): void {
    const tickers = (store.get('tickers') as Ticker[])
      .map(h => (h.Ticker ?? '').toUpperCase().trim()).filter(Boolean).slice(0, 10);
    if (!tickers.length) { showToast('No portfolio tickers — sync Holdings or enter manually', 'warn'); return; }
    (document.getElementById('prob-ticker-input') as HTMLInputElement).value = tickers.join(', ');
    this.load(tickers);
  }

  private async load(tickers: string[]): Promise<void> {
    this.currentTickers = tickers;
    const alpacaKey    = localStorage.getItem('meridian_alpaca_key')    ?? '';
    const alpacaSecret = localStorage.getItem('meridian_alpaca_secret') ?? '';

    if (!alpacaKey || !alpacaSecret) {
      this.el('prob-body').innerHTML = `
        <div class="empty-state">
          <div style="font-size:22px;margin-bottom:8px;">⚙</div>
          <div style="font-family:var(--mono);font-size:12px;color:var(--warn);">Alpaca API keys required</div>
          <div style="font-size:11px;color:var(--text3);margin-top:6px;">Open <kbd style="background:var(--bg3);border:1px solid var(--border);padding:1px 5px;border-radius:2px;font-family:var(--mono);">Ctrl+Shift+,</kbd></div>
        </div>`;
      return;
    }

    this.el('prob-body').innerHTML = `<div style="padding:20px;color:var(--text3);font-family:var(--mono);font-size:12px;">Calibrating probabilities for ${tickers.length} ticker${tickers.length>1?'s':''}…</div>`;

    try {
      const data = await workerFetch<ProbResp>('/api/probability', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tickers, alpacaKey, alpacaSecret }),
        signal: AbortSignal.timeout(120000),
      });
      document.getElementById('prob-updated')!.textContent = `Cached ${timeAgo(data.cachedAt)}`;
      this.renderResults(data.results);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      this.el('prob-body').innerHTML = `<div style="padding:20px;color:var(--red);font-family:var(--mono);font-size:12px;">Error: ${esc(msg)}</div>`;
    }
  }

  private renderResults(results: ProbResult[]): void {
    const rows = results.map(r => {
      const sigCol = SIG_COLOR[r.signal];
      const sigBg  = SIG_BG[r.signal];
      const pct    = Math.round(r.probability * 100);
      const kellyPct = Math.round(r.kelly * 100);
      const chgCol = r.change20d >= 0 ? 'var(--green)' : 'var(--red)';

      const detailContent = `
        <div style="display:flex;gap:24px;flex-wrap:wrap;padding:14px 20px;background:var(--bg2);">
          <div style="min-width:180px;">
            <div style="font-size:10px;letter-spacing:0.1em;color:var(--text3);text-transform:uppercase;margin-bottom:8px;">Component Probabilities</div>
            ${compBar(r.components.momentum,    'Momentum')}
            ${compBar(r.components.sentiment,   'Sentiment')}
            ${compBar(r.components.fundamentals,'Fundamentals')}
          </div>
          <div style="min-width:180px;">
            <div style="font-size:10px;letter-spacing:0.1em;color:var(--text3);text-transform:uppercase;margin-bottom:8px;">Key Factors</div>
            ${[...r.factors.momentum, ...r.factors.sentiment, ...r.factors.fundamentals]
              .filter(Boolean).slice(0,8).map(f => `<div style="font-size:11px;color:var(--text2);margin-bottom:2px;">• ${esc(f)}</div>`).join('')}
          </div>
          ${r.history.length > 1 ? `
          <div style="min-width:200px;">
            <div style="font-size:10px;letter-spacing:0.1em;color:var(--text3);text-transform:uppercase;margin-bottom:8px;">Prediction Log</div>
            ${r.history.slice(0,5).map(h => `
              <div style="display:flex;gap:10px;font-size:11px;margin-bottom:3px;">
                <span style="color:${SIG_COLOR[h.signal as 'BET'|'PASS'|'FADE']??'var(--text3)'};font-family:var(--mono);font-weight:700;min-width:36px;">${esc(h.signal)}</span>
                <span style="color:var(--accent);font-family:var(--mono);">${Math.round(h.prob*100)}%</span>
                <span style="color:var(--text3);">${timeAgo(h.ts)}</span>
                ${h.price ? `<span style="color:var(--text3);">@ $${h.price.toFixed(2)}</span>` : ''}
              </div>`).join('')}
          </div>` : ''}
        </div>`;

      return `
        <tr data-prob-expand="${esc(r.ticker)}" style="cursor:pointer;border-bottom:1px solid var(--border);">
          <td style="padding:12px 14px;">
            <span style="font-family:var(--mono);font-size:13px;font-weight:700;color:var(--accent);">${esc(r.ticker)}</span>
            ${r.price ? `<div style="font-size:10px;color:var(--text3);font-family:var(--mono);">$${r.price.toFixed(2)}</div>` : ''}
          </td>
          <td style="padding:12px 14px;">${probGauge(r.probability, 56)}</td>
          <td style="padding:12px 14px;">
            <span style="background:${sigBg};border:1px solid ${sigCol};color:${sigCol};font-family:var(--mono);font-size:12px;font-weight:700;padding:4px 12px;border-radius:2px;letter-spacing:0.1em;">${esc(r.signal)}</span>
          </td>
          <td style="padding:12px 14px;font-family:var(--mono);font-size:13px;font-weight:700;color:${r.kelly > 0 ? 'var(--accent)' : 'var(--text3)'};">${kellyPct > 0 ? kellyPct + '%' : '—'}</td>
          <td style="padding:12px 14px;font-family:var(--mono);font-size:12px;color:${chgCol};">${r.change20d >= 0 ? '+' : ''}${r.change20d}%</td>
          <td style="padding:12px 14px;font-family:var(--mono);font-size:12px;color:${r.rsi > 70 ? 'var(--warn)' : r.rsi < 30 ? 'var(--red)' : 'var(--text)'};">${r.rsi}</td>
          <td style="padding:12px 14px;">
            <div style="width:${r.confidence}px;max-width:80px;height:4px;background:var(--bg3);border-radius:2px;">
              <div style="width:${r.confidence}%;height:100%;background:var(--accent);border-radius:2px;"></div>
            </div>
            <div style="font-size:10px;color:var(--text3);margin-top:2px;">${r.confidence}%</div>
          </td>
          <td style="padding:12px 14px;font-size:10px;color:var(--text3);">▼</td>
        </tr>
        <tr id="prob-detail-${esc(r.ticker)}" style="display:none;">
          <td colspan="8" style="padding:0;">${detailContent}</td>
        </tr>`;
    }).join('');

    this.el('prob-body').innerHTML = `
      <div style="background:var(--panel);border:1px solid var(--border);border-radius:4px;padding:12px 16px;margin-bottom:16px;font-size:11px;color:var(--text3);line-height:1.6;">
        Probabilities calibrated via Platt scaling (logistic transform) on momentum · sentiment · fundamentals components.
        <strong style="color:var(--green);">BET</strong> ≥62% · <strong style="color:var(--warn);">PASS</strong> 45–62% · <strong style="color:var(--red);">FADE</strong> ≤45%
        · Kelly % = recommended position size fraction · predictions logged for future re-calibration.
      </div>
      <table style="width:100%;border-collapse:collapse;font-size:12px;">
        <thead>
          <tr style="border-bottom:2px solid var(--border);">
            <th style="padding:7px 14px;text-align:left;font-size:10px;color:var(--text3);text-transform:uppercase;letter-spacing:0.1em;">Ticker</th>
            <th style="padding:7px 14px;text-align:left;font-size:10px;color:var(--text3);text-transform:uppercase;letter-spacing:0.1em;">Probability</th>
            <th style="padding:7px 14px;text-align:left;font-size:10px;color:var(--text3);text-transform:uppercase;letter-spacing:0.1em;">Signal</th>
            <th style="padding:7px 14px;text-align:left;font-size:10px;color:var(--text3);text-transform:uppercase;letter-spacing:0.1em;" title="Kelly Criterion position sizing">Kelly Size</th>
            <th style="padding:7px 14px;text-align:left;font-size:10px;color:var(--text3);text-transform:uppercase;letter-spacing:0.1em;">20d Change</th>
            <th style="padding:7px 14px;text-align:left;font-size:10px;color:var(--text3);text-transform:uppercase;letter-spacing:0.1em;">RSI</th>
            <th style="padding:7px 14px;text-align:left;font-size:10px;color:var(--text3);text-transform:uppercase;letter-spacing:0.1em;">Confidence</th>
            <th style="padding:7px 14px;"></th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>
      <div style="font-size:10px;color:var(--text3);text-align:right;padding:8px 2px;margin-top:4px;">
        Click row to expand component breakdown and prediction log · 1-hour cache
      </div>`;
  }
}
